<template>
  <layout v-if="profileLoaded">
    <template v-slot:title>Create Content Block</template>
    <danger
      v-if="!contentSelected"
      title="You need to select some content in the document."
    ></danger>
    <div>
      <form @submit.prevent="submitted">
        <div class="form-group">
          <text-field
            id="title"
            label="Title"
            placeholder="The title for the block."
            v-model="v$.title.$model"
            mandatory
          >
            <template v-slot:validation v-if="v$.title.$error">
              <validation-error v-if="!v$.title.required"
                >You must specify a title.</validation-error
              >
            </template>
          </text-field>
          <text-field
            multiline
            id="description"
            label="Description"
            placeholder="A brief description of the block."
            v-model="v$.description.$model"
            mandatory
          >
            <template v-slot:validation v-if="v$.description.$error">
              <validation-error v-if="!v$.description.required"
                >You must provide a description.</validation-error
              >
            </template>
          </text-field>
          <switch-field
            id="avail-template-authors"
            v-model="availableForTemplateAuthors"
            v-if="isDocumentAdmin"
            label="Enable for Template Authors"
          ></switch-field>
          <switch-field
            id="avail-document-authors"
            v-model="availableForDocumentAuthors"
            v-if="isDocumentAdmin"
            label="Enable for Document Authors"
          ></switch-field>
          <tag-field
            label="Tags"
            v-model="tags"
            :existing-tags="existingTags"
          ></tag-field>
          <form-buttons>
            <button
              class="btn btn-primary"
              :disabled="v$.$invalid || !contentSelected"
            >
              Create
            </button>
            <router-link to="/contentblocks" class="btn btn-secondary"
              >Cancel</router-link
            >
          </form-buttons>
        </div>
      </form>
    </div>
  </layout>
</template>
<script>
import { mapState } from "vuex";
import { required } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import templateTypes from "@/services/sharedo/templateTypesService";
import contentBlocks from "@/services/sharedo/contentBlocksService";
import systemNames from "@/services/sharedo/systemNamesService";
import docGenTags from "@/services/sharedo/docGenTagsService";
import imageData from "@/services/word/imageDataService";
import imageUrlSubtitutor from "@/services/html/imageUrlSubstitutor";
import ValidationError from "../../components/common/forms/ValidationError.vue";
import TagField from "@/components/common/tags/TagField.vue";
import Danger from "@/components/common/banners/Danger.vue";
import { ascending } from "@/util/sort";
import { generateSystemName, withErrorHandling } from "@/util/sharedoUtil";
import profile from "@/mixins/profile";
import { ADMIN_DOCUMENT_ASSEMBLY } from "@/constants/permissions";

const loadExistingTags = async function() {
  this.logDebug("Loading existing tags.");

  try {
    const response = await docGenTags.getAll();
    if (response.tags.length > 0) {
      const tags = response.tags.map((t) => ({ id: t, value: t }));
      tags.sort(ascending((x) => x.value));

      this.existingTags = tags;
      this.logInfo("Loaded existing tags.", { tags: this.existingTags });
    }
  } catch (error) {
    this.logError("Unable to load existing tags.", { error });
    throw error;
  }
};

const beforeMount = async function() {
  this.logDebug("Loading template type.");

  try {
    const types = await templateTypes.get();
    if (types && types.length > 0) {
      this.templateTypes = types.map((t) => ({
        label: t.name,
        value: t.systemName,
      }));

      this.templateType = types[0].systemName;

      this.logInfo("Loaded template type.", {
        templateType: this.templateType,
      });
    }
  } catch (error) {
    this.logError("Unable to load template type.", { error });
    throw error;
  }
};

const submitted = async function() {
  this.v$.$touch();

  if (this.v$.$invalid) {
    this.submitStatus = "ERROR";
  } else{
    this.submitStatus = "PENDING";

    this.logDebug("Creating new content block.");

    try {
      await Word.run(async (context) => {
        const selection = context.document.getSelection();
        const images = await imageData.extractFrom(selection);
        const htmlContent = selection.getHtml();
        await context.sync();

        this.logDebug("Extracted HTML content.", { htmlContent });

        if (htmlContent.value) {
          const html = imageUrlSubtitutor.substitute(htmlContent.value, images);
          let systemName = generateSystemName(this.title);

          let suffix = 0;
          let sysNameCheck;
          do {
            if (suffix > 0) {
              systemName = `${systemName}${suffix}`;
            }

            sysNameCheck = await systemNames.check(
              "document-template",
              systemName
            );
            suffix++;
          } while (sysNameCheck.exists);

          this.logDebug("Generated new system name.", { systemName });

          const newContentBlock = {
            systemName: systemName,
            templateType: this.templateType,
            title: this.title,
            description: this.description,
            htmlContent: html,
            tags: this.tags,
            availableForDocumentAuthors: this.availableForDocumentAuthors,
            availableForTemplateAuthors: this.isDocumentAdmin
              ? this.availableForTemplateAuthors
              : false,
          };

          await contentBlocks.add(newContentBlock);

          this.logInfo("Created new content block.", {
            contentBlock: newContentBlock,
          });

          this.$router.push("/contentblocks");
        }
      });
    } catch (error) {
      this.logError("Unable to create content block.", { error });
      throw error;
    }
  }
};

export default {
  setup() {
    return { v$: useVuelidate() }
  },
  components: { TagField, ValidationError, Danger },
  mixins: [profile],
  data: function() {
    return {
      title: "",
      description: "",
      templateType: "",
      availableForDocumentAuthors: true,
      availableForTemplateAuthors: true,
      templateTypes: [],
      tags: [],
      existingTags: [],
    };
  },
  computed: {
    ...mapState({
      contentSelected: (state) => state.contentSelected,
    }),
    isDocumentAdmin: function() {
      return this.hasPermission(ADMIN_DOCUMENT_ASSEMBLY);
    },
  },
  validations() {
    return {
      title: {
        required,
      },
      description: {
        required,
      },
    };
  },
  methods: {
    submitted: withErrorHandling(submitted),
  },
  beforeMount,
  mounted: loadExistingTags,
};
</script>
